import React, { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plane, RefreshCw, Crosshair, Loader2 } from "lucide-react";
import { tileUrl, tileAttribution, TILE_MAX_ZOOM } from "@/lib/mapTiles";
import { useTheme } from "@/contexts/ThemeContext";
import { getAccurateLocation } from "@/lib/geolocate";

// Aircraft type → size + silhouette category
// "heavy" wide-body, "narrow" airliner, "regional" turboprop/regional jet, "small" GA, "helo" helicopter
const TYPE_CATEGORY = {
  // Heavies
  A380: "heavy", A388: "heavy", B748: "heavy", B744: "heavy", B772: "heavy", B773: "heavy",
  B77L: "heavy", B77W: "heavy", B788: "heavy", B789: "heavy", B78X: "heavy", A346: "heavy",
  A359: "heavy", A35K: "heavy", A332: "heavy", A333: "heavy", A338: "heavy", A339: "heavy",
  B764: "heavy", B763: "heavy", MD11: "heavy",
  // Narrow-bodies
  A319: "narrow", A320: "narrow", A321: "narrow", A20N: "narrow", A21N: "narrow", A19N: "narrow",
  B737: "narrow", B738: "narrow", B739: "narrow", B73G: "narrow", B73H: "narrow", B73W: "narrow",
  B38M: "narrow", B39M: "narrow", B752: "narrow", B753: "narrow",
  // Regional jets / turboprops
  CRJ7: "regional", CRJ9: "regional", CRJ2: "regional", CRJX: "regional",
  E170: "regional", E175: "regional", E190: "regional", E195: "regional", E290: "regional", E295: "regional",
  DH8D: "regional", DH8C: "regional", DH8B: "regional", AT72: "regional", AT76: "regional",
  // Helicopters
  EC35: "helo", EC30: "helo", EC20: "helo", EC25: "helo", AS50: "helo", AS55: "helo",
  R44: "helo", R66: "helo", B06: "helo", B407: "helo", S76: "helo", S92: "helo", H60: "helo",
};

function categorize(t) {
  if (!t) return "small";
  const up = t.toUpperCase();
  if (TYPE_CATEGORY[up]) return TYPE_CATEGORY[up];
  // Heuristics
  if (up.startsWith("B7") || up.startsWith("A3")) return up.match(/[78][0-9]/) ? "heavy" : "narrow";
  if (up.startsWith("E1") || up.startsWith("E2") || up.startsWith("CRJ") || up.startsWith("AT")) return "regional";
  if (up.startsWith("EC") || up.startsWith("AS") || up.startsWith("S7") || up.startsWith("R4") || up.startsWith("H6")) return "helo";
  return "small";
}

const CATEGORY_SIZE = { heavy: 30, narrow: 24, regional: 20, small: 16, helo: 18 };

// Top-down silhouette of a fixed-wing aircraft — wings + fuselage + horizontal stabilizer.
const PLANE_PATH = "M12 1.6 L13.3 9.4 L22.4 12 L22.4 13.6 L13.3 12.6 L13.3 17.6 L15.2 19.6 L15.2 21 L12 19.9 L8.8 21 L8.8 19.6 L10.7 17.6 L10.7 12.6 L1.6 13.6 L1.6 12 L10.7 9.4 Z";

function planeIcon(heading = 0, type, selected = false) {
  const cat = categorize(type);
  const size = CATEGORY_SIZE[cat] + (selected ? 4 : 0);
  const fill = selected
    ? "#facc15"
    : cat === "heavy" ? "#67e8f9"
    : cat === "narrow" ? "#22d3ee"
    : cat === "regional" ? "#60a5fa"
    : cat === "helo" ? "#a78bfa"
    : "#cbd5e1";
  const stroke = selected ? "#1e293b" : "rgba(15,23,42,0.95)";
  // Helo silhouette: fuselage with sweeping rotor disk + tail boom.
  if (cat === "helo") {
    return L.divIcon({
      html: `<div style="transform: rotate(${heading}deg); width:${size}px; height:${size}px; display:flex; align-items:center; justify-content:center;">
        <svg viewBox="0 0 24 24" width="${size}" height="${size}" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.6));">
          <ellipse cx="12" cy="12" rx="11" ry="1.2" fill="${fill}" opacity="0.35"/>
          <ellipse cx="12" cy="12" rx="3.6" ry="2.4" fill="${fill}" stroke="${stroke}" stroke-width="0.7"/>
          <rect x="11.3" y="13.5" width="1.4" height="7" fill="${fill}" stroke="${stroke}" stroke-width="0.6"/>
          <rect x="9.5" y="20" width="5" height="1.2" fill="${fill}" stroke="${stroke}" stroke-width="0.6"/>
        </svg>
      </div>`,
      className: "", iconSize: [size, size], iconAnchor: [size / 2, size / 2],
    });
  }
  return L.divIcon({
    html: `<div style="transform: rotate(${heading}deg); width:${size}px; height:${size}px; display:flex; align-items:center; justify-content:center;">
      <svg viewBox="0 0 24 24" width="${size}" height="${size}" style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.7));">
        <path d="${PLANE_PATH}" fill="${fill}" stroke="${stroke}" stroke-width="0.9" stroke-linejoin="round"/>
        <circle cx="12" cy="11" r="0.9" fill="${stroke}" opacity="0.4"/>
      </svg>
    </div>`,
    className: "", iconSize: [size, size], iconAnchor: [size / 2, size / 2],
  });
}

function FlyTo({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center) map.flyTo(center, zoom || map.getZoom(), { duration: 0.6 });
  }, [center, zoom, map]);
  return null;
}

function BoundsReader({ onChange }) {
  const map = useMap();
  useEffect(() => {
    // initial
    const b = map.getBounds();
    onChange({ lamin: b.getSouth(), lomin: b.getWest(), lamax: b.getNorth(), lomax: b.getEast() });
  }, [map, onChange]);
  useMapEvents({
    moveend: (e) => {
      const b = e.target.getBounds();
      onChange({ lamin: b.getSouth(), lomin: b.getWest(), lamax: b.getNorth(), lomax: b.getEast() });
    },
    zoomend: (e) => {
      const b = e.target.getBounds();
      onChange({ lamin: b.getSouth(), lomin: b.getWest(), lamax: b.getNorth(), lomax: b.getEast() });
    },
  });
  return null;
}

function fmtAlt(ft) {
  if (ft == null) return "—";
  if (typeof ft === "string") return ft.toUpperCase();
  return `${Math.round(ft).toLocaleString()} ft`;
}
function fmtSpd(ms) {
  if (ms == null) return "—";
  return `${Math.round(ms * 1.94384)} kt`;
}

export default function ATCPage() {
  const { theme } = useTheme();
  const [flights, setFlights] = useState([]);
  const [bbox, setBbox] = useState(null); // { lamin, lomin, lamax, lomax }
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [auto, setAuto] = useState(true);
  const [source, setSource] = useState("");
  const [fetchTime, setFetchTime] = useState(null);
  const [tilesUsed, setTilesUsed] = useState(0);
  const [mapCenter, setMapCenter] = useState([40.7128, -74.006]);
  const [nowMs, setNowMs] = useState(Date.now());
  const timerRef = useRef(null);
  const animRef = useRef(null);
  const flightsRef = useRef(new Map());

  useEffect(() => {
    animRef.current = setInterval(() => setNowMs(Date.now()), 250);
    return () => clearInterval(animRef.current);
  }, []);

  const fetchFlights = async (b) => {
    if (!b) return;
    setLoading(true);
    try {
      const bboxStr = `${b.lamin.toFixed(3)},${b.lomin.toFixed(3)},${b.lamax.toFixed(3)},${b.lomax.toFixed(3)}`;
      const { data } = await api.get("/atc/flights", { params: { bbox: bboxStr } });
      const incoming = data.flights || [];
      const fetchedAt = Date.now() / 1000;
      const newMap = new Map();
      incoming.forEach((f) => newMap.set(f.icao24, { ...f, fetchedAt }));
      flightsRef.current = newMap;
      setFlights(incoming);
      setSource(data.source || "");
      setFetchTime(data.time || null);
      setTilesUsed(data.tiles || 1);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  // Debounced bbox change → fetch
  const bboxKey = bbox ? `${bbox.lamin.toFixed(2)}|${bbox.lomin.toFixed(2)}|${bbox.lamax.toFixed(2)}|${bbox.lomax.toFixed(2)}` : "";
  useEffect(() => {
    if (!bbox) return;
    const id = setTimeout(() => fetchFlights(bbox), 300);
    return () => clearTimeout(id);
  }, [bboxKey]);

  // Live polling every 10s on current bbox
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (auto && bbox) timerRef.current = setInterval(() => fetchFlights(bbox), 10000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [bboxKey, auto]);

  const useMyLocation = () => {
    getAccurateLocation({
      onSuccess: ({ lat, lon }) => setMapCenter([lat, lon]),
    });
  };

  // Smoothly dead-reckon flights between fetches: advance lat/lon by velocity along heading
  const liveFlights = useMemo(() => {
    const nowSec = nowMs / 1000;
    return flights.map((f) => {
      if (f.lat == null || f.lon == null || f.velocity == null || f.heading == null) return f;
      const last = flightsRef.current.get(f.icao24);
      const fetchedAt = last?.fetchedAt || nowSec;
      const dt = Math.max(0, Math.min(20, nowSec - fetchedAt));
      const distM = f.velocity * dt;
      const headingRad = (f.heading || 0) * Math.PI / 180;
      const dLat = (distM * Math.cos(headingRad)) / 111320;
      const cosLat = Math.cos(f.lat * Math.PI / 180) || 1;
      const dLon = (distM * Math.sin(headingRad)) / (111320 * cosLat);
      return { ...f, lat: f.lat + dLat, lon: f.lon + dLon };
    });
  }, [flights, nowMs]);

  const liveSelected = useMemo(() => {
    if (!selected) return null;
    return liveFlights.find((f) => f.icao24 === selected.icao24) || selected;
  }, [liveFlights, selected]);

  const airborne = useMemo(
    () => liveFlights.filter((f) => f.lat != null && f.lon != null && !f.on_ground),
    [liveFlights]
  );

  return (
    <div className="absolute inset-0 flex flex-col" data-testid="atc-page">
      {/* Top status bar */}
      <div className="h-12 border-b border-border bg-card/80 backdrop-blur flex items-center justify-between px-4 shrink-0 z-10">
        <div className="flex items-center gap-3">
          <Plane className="w-4 h-4 text-cyan-400" />
          <div className="font-mono text-xs tracking-widest text-primary">// ATC LIVE</div>
          <Badge variant="outline" className="border-cyan-400/40 text-cyan-400" data-testid="atc-count">
            {airborne.length} AIRBORNE
          </Badge>
          <span className="font-mono text-[10px] text-muted-foreground">
            {source.toUpperCase() || "—"} · {fetchTime ? new Date(fetchTime * 1000).toLocaleTimeString() : "—"} · {tilesUsed} tile{tilesUsed === 1 ? "" : "s"}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={useMyLocation} data-testid="atc-my-location-btn">
            <Crosshair className="w-3 h-3 mr-1" /> My Region
          </Button>
          <Button
            size="sm"
            variant={auto ? "default" : "outline"}
            onClick={() => setAuto((v) => !v)}
            data-testid="atc-auto-btn"
          >
            {auto ? "LIVE ON" : "LIVE OFF"}
          </Button>
          <Button size="icon" variant="ghost" onClick={() => fetchFlights(bbox)} data-testid="atc-refresh-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative min-h-0">
        <MapContainer center={mapCenter} zoom={8} style={{ height: "100%", width: "100%" }}>
          <TileLayer key={theme} url={tileUrl(theme)} attribution={tileAttribution()} maxZoom={TILE_MAX_ZOOM} />
          <FlyTo center={mapCenter} zoom={8} />
          <BoundsReader onChange={setBbox} />
          {airborne.map((f) => (
            <Marker
              key={f.icao24}
              position={[f.lat, f.lon]}
              icon={planeIcon(f.heading || 0, f.aircraft_type, selected?.icao24 === f.icao24)}
              eventHandlers={{ click: () => setSelected(f) }}
            >
              <Popup>
                <div className="font-mono text-xs space-y-0.5">
                  <div className="font-bold text-sm">{f.callsign || f.icao24?.toUpperCase()}</div>
                  {f.aircraft_type && <div>Type: {f.aircraft_type}</div>}
                  {f.registration && <div>Reg: {f.registration}</div>}
                  <div>Alt: {fmtAlt(f.baro_altitude ?? f.geo_altitude)}</div>
                  <div>Spd: {fmtSpd(f.velocity)} · Hdg: {Math.round(f.heading || 0)}°</div>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>

        {/* Floating flight detail card */}
        {selected && (
          <div className="absolute top-3 right-3 z-[400] w-80 bg-card/95 backdrop-blur border border-border rounded-md p-4 shadow-2xl" data-testid="flight-detail-card">
            <div className="flex items-start justify-between mb-3">
              <div>
                <div className="font-mono text-xs text-primary tracking-widest">// FLIGHT DETAIL</div>
                <div className="text-xl font-bold mono text-cyan-400 mt-1">{selected.callsign || selected.icao24?.toUpperCase()}</div>
                <div className="text-xs text-muted-foreground">
                  {selected.aircraft_type ? `${selected.aircraft_type} · ` : ""}{selected.registration || selected.origin_country || ""}
                </div>
              </div>
              <Button size="icon" variant="ghost" onClick={() => setSelected(null)}>✕</Button>
            </div>
            <div className="grid grid-cols-2 gap-2 font-mono text-xs">
              <div className="border border-border p-2 rounded-sm">
                <div className="text-muted-foreground text-[10px]">CALLSIGN</div>
                <div className="font-bold">{selected.callsign || "—"}</div>
              </div>
              <div className="border border-border p-2 rounded-sm">
                <div className="text-muted-foreground text-[10px]">SQUAWK</div>
                <div className="font-bold">{selected.squawk || "—"}</div>
              </div>
              <div className="border border-border p-2 rounded-sm">
                <div className="text-muted-foreground text-[10px]">ALT (BARO)</div>
                <div className="font-bold">{fmtAlt(selected.baro_altitude)}</div>
              </div>
              <div className="border border-border p-2 rounded-sm">
                <div className="text-muted-foreground text-[10px]">GROUND SPD</div>
                <div className="font-bold">{fmtSpd(selected.velocity)}</div>
              </div>
              <div className="border border-border p-2 rounded-sm">
                <div className="text-muted-foreground text-[10px]">HEADING</div>
                <div className="font-bold">{Math.round(selected.heading || 0)}°</div>
              </div>
              <div className="border border-border p-2 rounded-sm">
                <div className="text-muted-foreground text-[10px]">V/S</div>
                <div className="font-bold">{selected.vertical_rate != null ? `${Math.round(selected.vertical_rate * 196.85)} fpm` : "—"}</div>
              </div>
              <div className="border border-border p-2 rounded-sm col-span-2">
                <div className="text-muted-foreground text-[10px]">POSITION</div>
                <div className="font-bold">{selected.lat?.toFixed(4)}, {selected.lon?.toFixed(4)}</div>
              </div>
              <div className="border border-border p-2 rounded-sm col-span-2">
                <div className="text-muted-foreground text-[10px]">AIRCRAFT</div>
                <div className="font-bold">{selected.aircraft_type || "—"} · ICAO {selected.icao24} · DST {selected.distance_nm != null ? `${Math.round(selected.distance_nm)} nm` : "—"}</div>
              </div>
            </div>
            <div className="text-[10px] text-muted-foreground mt-3 font-mono">
              Data: adsb.lol (ADS-B Exchange community).
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
